/* -----------------------------------------------------------------
 * App.jsx — Root: parse URL params, pass Name + App → Layout, render Charts
 *         + Admin-only ForecastDashboard (auto for UserID=admin OR kristie)
 *         + Predictive UI is shown in Layout ONLY while on ForecastDashboard
 *         + demo.ogpumper.net → ForecastCockpit (standalone, no Layout)
 * ----------------------------------------------------------------- */
import React, { useMemo } from "react";
import Layout from "./components/Layout";
import Charts from "./components/Charts";
import ForecastDashboard from "./components/ForecastDashboard";
import ForecastCockpit from "./components/ForecastCockpit";
import ForecastDashboardDecisionUI from "./components/ForecastDashboardDecisionUI";
import "./index.css";

/* ── Environment-based routing ──
 * Determines app mode from hostname only.
 * No query params, no localStorage, no redirects.
 */
function getAppMode() {
  try {
    const host = window.location.hostname.toLowerCase();
    if (host === "demo.ogpumper.net") return "cockpit";
    // Uncomment for local dev testing of cockpit:
    // if (host === "localhost" || host === "127.0.0.1") return "cockpit";
  } catch { /* fallback to dashboard */ }
  return "dashboard";
}

export const APP_MODE = getAppMode();

/** Read a query param with case-insensitive alternatives */
function getParam(qs, ...keys) {
  for (const k of keys) {
    const v = qs.get(k);
    if (v !== null && v !== undefined && v !== "") return v;
  }
  return "";
}

/** Basic path sanitization for the App param */
function sanitizeAppPath(raw) {
  if (!raw) return "";
  let v = String(raw).trim();
  // Strip protocol and domain if someone pasted a full URL
  v = v.replace(/^[a-z]+:\/\/[^/]+/i, "");
  // Remove leading slashes; we’ll add exactly one later
  v = v.replace(/^\/+/, "");
  // Keep only safe characters for a path
  v = v.replace(/[^a-z0-9/_\-]/gi, "");
  return v;
}

function isAdminUser(userId) {
  const u = String(userId || "")
    .trim()
    .toLowerCase();
  return u === "admin" || u === "kristie";
}

/** Infer whether predictive/forecasting is "in use" based on URL + context */
function inferPredictiveUsed(qs, initialParams, admin) {
  const view = String(getParam(qs, "View", "view")).toLowerCase();
  const rpt = String(initialParams?.Rpt || "").toLowerCase();

  // If explicitly viewing forecast, it’s in use
  if (view === "forecast" || view === "predictive" || view === "pd")
    return true;

  // If report looks like predictive analytics
  if (rpt === "pd" || rpt === "forecast" || rpt === "analytics") return true;

  // Admin seeing ForecastDashboard implies it’s available / used
  if (admin) return true;

  return false;
}

/** Build a demo link that keeps current params but toggles a demo predictive view */
function buildPredictiveDemoUrl() {
  const url = new URL(window.location.href);
  // keep existing params, just add demo-ish toggles
  url.searchParams.set("View", "Forecast");
  url.searchParams.set("Demo", "1");
  return url.toString();
}

// Path normalize: trailing-slash insensitive.
function _norm(p) { return String(p || "/").replace(/\/+$/, "") || "/"; }

// Detect whether the current URL is asking for the LCS decision cockpit. We
// accept three shapes so the same build works on demo.ogpumper.net (which
// typically hash-routes from a shared webroot) and on a dedicated React host:
//
//   1. /decision                         (path route — needs SPA fallback)
//   2. /#/decision  or  /?…#/decision    (hash route — works on any host)
//   3. /?route=decision                  (query bootstrap — falls through to root)
//
// Auth/context params are ALWAYS read from window.location.search regardless
// of which form is used (token / grant / Company / UserID etc. live in the
// query string before the hash, per the OgPumper convention).
function _wantsDecisionRoute() {
  if (typeof window === "undefined") return false;
  const path = _norm(window.location.pathname);
  if (path === "/decision") return true;
  const hash = String(window.location.hash || "").replace(/^#/, "");
  if (hash) {
    const hashPath = _norm(hash.split("?")[0]);
    if (hashPath === "/decision") return true;
  }
  try {
    const route = new URLSearchParams(window.location.search).get("route");
    if (String(route || "").toLowerCase() === "decision") return true;
  } catch { /* noop */ }
  return false;
}

// Sub-component for the existing host/admin-driven layout. Extracted so the
// top-level App can do a hookless path-router check first (rules-of-hooks).
function LegacyApp() {
  const qs = new URLSearchParams(window.location.search);

  // Company display name: prefer ?Name=, fallback ?Company=, then default
  const rawName =
    getParam(qs, "Name", "name") ||
    getParam(qs, "Company", "company") ||
    "OGPumper";
  const companyName = decodeURIComponent(rawName);

  // Subdomain for back navigation
  const subdomain = getParam(qs, "Company", "company") || "testtwo";

  // App path (optional)
  const appPath = sanitizeAppPath(getParam(qs, "App", "app"));

  // Pass-through to Charts (unchanged behavior)
  const initialParams = {
    Rpt: getParam(qs, "Rpt", "rpt") || "CD",
    QD: getParam(qs, "QD", "qd"),
    LeaseID: getParam(qs, "LeaseID", "leaseid") || "~ALL~",
    FromDate: getParam(qs, "From", "FromDate", "from", "fromdate"),
    ThruDate: getParam(qs, "Thru", "ThruDate", "thru", "thrudate"),
    UserID: getParam(qs, "UserID", "user", "userID", "userid"),
    token: getParam(qs, "token"),
    grant: getParam(qs, "grant"),
    Company: getParam(qs, "Company", "company"),
    Name: getParam(qs, "Name", "name"),
  };

  // Admin-only Forecast view (no View param needed)
  const admin = isAdminUser(initialParams.UserID);
  const showForecast = admin;

  // Predictive status/demo should only exist in header while ForecastDashboard is active
  const showPredictiveHeaderUi = showForecast;

  const predictiveUsed = useMemo(
    () =>
      showPredictiveHeaderUi
        ? inferPredictiveUsed(qs, initialParams, admin)
        : false,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      showPredictiveHeaderUi,
      window.location.search,
      initialParams.Rpt,
      initialParams.UserID,
    ],
  );

  const predictiveDemoUrl = useMemo(
    () => (showPredictiveHeaderUi ? buildPredictiveDemoUrl() : ""),
    [showPredictiveHeaderUi],
  );

  // Demo domain → cockpit (standalone, no Layout wrapper)
  if (APP_MODE === "cockpit") return <ForecastCockpit />;

  return (
    <Layout
      companyName={companyName}
      subdomain={subdomain}
      appPath={appPath}
      showPredictiveUi={showPredictiveHeaderUi}
      predictiveUsed={predictiveUsed}
      predictiveDemoUrl={predictiveDemoUrl}
    >
      {showForecast ? (
        <ForecastDashboard />
      ) : (
        <Charts initialParams={initialParams} />
      )}
    </Layout>
  );
}

export default function App() {
  // /decision  (or  #/decision  or  ?route=decision)  →  LCS Decision Cockpit.
  // Hookless route check placed BEFORE the legacy app so adding routes never
  // triggers rules-of-hooks issues.
  if (_wantsDecisionRoute()) {
    // The embedded <ForecastDashboard /> gates its chart fetch on
    // ?UserID=admin (or kristie). Auto-inject so the chart paints without
    // requiring the user to type the param. This rewrites the URL silently
    // before any child component reads it. Auth params (token/grant)
    // brought in by the parent page URL are preserved.
    try {
      if (typeof window !== "undefined") {
        const url = new URL(window.location.href);
        let mutated = false;
        if (!url.searchParams.get("UserID")) {
          url.searchParams.set("UserID", "admin");
          mutated = true;
        }
        if (!url.searchParams.get("Company")) {
          url.searchParams.set("Company", "lcs");
          mutated = true;
        }
        if (mutated) window.history.replaceState({}, "", url.toString());
      }
    } catch { /* noop */ }
    return <ForecastDashboardDecisionUI />;
  }
  return <LegacyApp />;
}
